"use client"

import newStyled from "@emotion/styled"
import Image from "next/image"
import { ComponentProps, RefObject } from "react"
import { Desktop, Mobile } from "./MediaQueries"

const VIDEO_EXTENSIONS = new Set(["mp4", "webm", "mov"])

export type MediaRendererProps = {
  mediaUrl?: string
  alt: string
  priority?: boolean
  variant?: "desktop" | "mobile"
  videoRef?: RefObject<HTMLVideoElement | null>
  videoProps?: ComponentProps<"video">
  isStatic?: boolean
  quality?: number
}

export const getExtension = (mediaUrl?: string) =>
  mediaUrl?.split("?")?.[0]?.split(".")?.pop()?.toLowerCase() || ""

export const isVideo = (mediaUrl?: string) =>
  VIDEO_EXTENSIONS.has(getExtension(mediaUrl))

export const getVideoType = (extension: string) => {
  if (extension === "mov") {
    return "video/quicktime"
  }
  return `video/${extension || "mp4"}`
}

const StyledVideo = newStyled.video<{ $isstatic?: boolean }>`
  width: 100%;
  height: 100%;
  border-radius: 20px;
  
  position: ${(props) => (props.$isstatic ? "static" : "absolute")};
  top: 0;
  left: 0;
`

const renderMediaElement = ({
  mediaUrl,
  alt,
  priority,
  variant,
  videoRef,
  videoProps,
  isStatic,
  quality,
}: MediaRendererProps) => {
  if (!mediaUrl) return null
  const extension = getExtension(mediaUrl)

  if (isVideo(mediaUrl)) {
    return (
      <StyledVideo
        key={`${variant ?? mediaUrl}-video`}
        autoPlay
        muted
        loop
        playsInline
        aria-label={`${alt} video`}
        ref={videoRef}
        {...videoProps}
      >
        <source src={mediaUrl} type={getVideoType(extension)} />
      </StyledVideo>
    )
  }

  return (
    <Image
      key={`${variant ?? mediaUrl}-image`}
      src={mediaUrl}
      priority={priority}
      alt={alt}
      {...(quality != null && { quality })}
      {...(!isStatic
        ? {
            fill: true,
          }
        : {
            width: "1024",
            height: "1024",
            sizes: "100vw",
          })}
    />
  )
}

type ResponsiveMediaProps = {
  desktopMedia?: string
  mobileMedia?: string
  alt: string
  mobileAlt?: string | null
  priority?: boolean
  desktopVideoRef?: RefObject<HTMLVideoElement | null>
  mobileVideoRef?: RefObject<HTMLVideoElement | null>
  videoProps?: ComponentProps<"video">
  isDesktopStatic?: boolean
  isMobileStatic?: boolean
  quality?: number
}

export const ResponsiveMedia = ({
  desktopMedia,
  mobileMedia,
  alt,
  mobileAlt,
  priority,
  desktopVideoRef,
  mobileVideoRef,
  videoProps,
  isDesktopStatic,
  isMobileStatic,
  quality,
}: ResponsiveMediaProps) => (
  <>
    {desktopMedia && (
      <Desktop>
        {renderMediaElement({
          mediaUrl: desktopMedia,
          alt,
          priority,
          variant: "desktop",
          videoRef: desktopVideoRef,
          videoProps,
          isStatic: isDesktopStatic,
          quality,
        })}
      </Desktop>
    )}
    {mobileMedia && (
      <Mobile>
        {renderMediaElement({
          mediaUrl: mobileMedia,
          alt: mobileAlt || alt,
          priority,
          variant: "mobile",
          videoRef: mobileVideoRef,
          videoProps,
          isStatic: isMobileStatic,
          quality,
        })}
      </Mobile>
    )}
  </>
)

export const MediaRenderer = (props: MediaRendererProps) =>
  renderMediaElement(props)
